import { useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Account } from "@shared/schema";

interface AccountSelectProps {
  value?: string | null;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clientGroupId?: string | null;
  entityTypeFilter?: "company" | "trust" | "partnership" | "sole_trader";
  selectedAccount?: Account | null;
  "data-testid"?: string;
}

const formatAccountLabel = (account: Account) =>
  account.tradingName
    ? `${account.legalName} (T/A ${account.tradingName})`
    : account.legalName;

export function AccountSelect({
  value,
  onValueChange,
  placeholder = "Select account",
  disabled = false,
  clientGroupId,
  entityTypeFilter,
  selectedAccount,
  "data-testid": testId,
}: AccountSelectProps) {
  const { data: accounts, isLoading } = useQuery<Account[]>({
    queryKey: clientGroupId
      ? ["/api/crm/accounts", { clientGroupId }]
      : ["/api/crm/accounts"],
  });

  const { data: selectedDetail } = useQuery<Account>({
    queryKey: ["/api/crm/accounts", value],
    enabled: !!value && !accounts?.some(account => account.id === value),
  });

  // Filter accounts by entity type if specified
  const filteredAccounts = entityTypeFilter
    ? accounts?.filter(account => !account.archivedAt && account.entityType === entityTypeFilter)
    : accounts?.filter(account => !account.archivedAt);
  const selectedAccountFromQuery = filteredAccounts?.find((account) => account.id === value);
  const resolvedSelectedAccount =
    selectedAccountFromQuery ??
    (selectedDetail?.id === value ? selectedDetail : null) ??
    (selectedAccount?.id === value ? selectedAccount : null);
  const shouldRenderFallbackOption =
    !!resolvedSelectedAccount &&
    !filteredAccounts?.some((account) => account.id === resolvedSelectedAccount.id);

  return (
    <Select
      value={value || undefined}
      onValueChange={onValueChange}
      disabled={disabled || isLoading}
    >
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={isLoading ? "Loading..." : placeholder}>
          {resolvedSelectedAccount ? formatAccountLabel(resolvedSelectedAccount) : undefined}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {shouldRenderFallbackOption && resolvedSelectedAccount && (
          <SelectItem value={resolvedSelectedAccount.id} disabled>
            {formatAccountLabel(resolvedSelectedAccount)}{resolvedSelectedAccount.archivedAt ? " (Archived)" : ""}
          </SelectItem>
        )}
        {filteredAccounts?.map((account) => (
          <SelectItem key={account.id} value={account.id}>
            {formatAccountLabel(account)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
